import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Stack,
  Alert,
  Skeleton,
  InputAdornment,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Description as DescriptionIcon,
  Public as PublicIcon,
  Factory as FactoryIcon,
} from "@mui/icons-material";
import InfoIcon from "@/assets/svgs/info.svg";
import { ModalV2, TextInput } from "@/components";
import { useCreateProductMutation } from "@/services/product-catalogue/pc-api";
import { useLazyCheckMetalPercentageRequiredQuery } from "@/services/product-catalogue/metal-percentage-api";
import { useGetCountrySummaryQuery, useGetHTSCodesQuery } from "@/services/dashboard/sm-api";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { CountrySelect, HTSCodeSelect } from "../product-select-fields";
import { formatHTSCode } from "@/utils";

interface CreateProductFormProps {
  open: boolean;
  onClose: () => void;
  onProductCreated: (productId?: string) => void;
  onTriggerPolling?: (productId: string, triggerReason: 'creation') => void;
  initialData?: {
    htsCode?: string;
    countryOfOrigin?: string;
    metalPercentage?: number;
  };
}

const CreateProductForm: React.FC<CreateProductFormProps> = ({
  open,
  onClose,
  onProductCreated,
  onTriggerPolling,
  initialData,
}) => {
  const { selectedClient } = useAppSelector((state: any) => state.client);
  const { selectedTenant } = useAppSelector((state: any) => state.tenantManagement);
  const { user } = useAppSelector((state: any) => state.auth);
  
  const [formData, setFormData] = useState({
    sku: "",
    name: "",
    description: "",
    htsCode: "",
    countryOfOrigin: "",
    metalPercentage: "",
  });

  const [showMetalPercentage, setShowMetalPercentage] = useState(false);
  const [metalRules, setMetalRules] = useState<any[]>([]);

  const [createProduct, { isLoading }] = useCreateProductMutation();
  const [checkMetalPercentage, { isLoading: isCheckingMetal }] = useLazyCheckMetalPercentageRequiredQuery();

  // Check if TD Admin with TRADEDIVE_CLIENT tenant
  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  // Get client ID - properly handle different user roles
  const clientId = useMemo(() => {
    // TD Admin with TRADEDIVE_CLIENT uses selectedTenant._id
    if (isTDAdminTCD) {
      return selectedTenant?._id;
    }
    // SP users use selectedClient._id
    if (selectedClient?._id) {
      return selectedClient._id;
    }
    // TD Client or SP Client uses their own user id (client/user ObjectId)
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
      return user?._id;
    }
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  // Fetch country data
  const { data: countrySummary, isLoading: isLoadingCountries } = useGetCountrySummaryQuery(
    { clientId: clientId || "" },
    { skip: !clientId || !open }
  );

  // Get all country codes for HTS query when no country is selected
  const allCountryCodes = useMemo(() => {
    if (!countrySummary?.getCountryOriginSummary) return [];
    return countrySummary.getCountryOriginSummary.map(country => country.country_code);
  }, [countrySummary]);

  // Fetch HTS codes based on selected country or all countries
  const { data: htsCodes, isLoading: isLoadingHTS } = useGetHTSCodesQuery(
    { 
      clientId: clientId || "",
      country_codes: formData.countryOfOrigin ? [formData.countryOfOrigin] : allCountryCodes,
    },
    { skip: !clientId || !open || allCountryCodes.length === 0 }
  );

  // Check if metal percentage is required when HTS code or country changes
  useEffect(() => {
    const checkMetal = async () => {
      if (formData.htsCode && formData.countryOfOrigin) {
        try {
          const result = await checkMetalPercentage({
            htsCode: formData.htsCode.replace(/\./g, ''), // Remove dots for API
            countryOfOrigin: formData.countryOfOrigin,
          }).unwrap();

          setShowMetalPercentage(result.isMetalPercentageRequired);
          setMetalRules(result.applicableMetalRules || []);
        } catch (error) {
          console.error("Error checking metal percentage requirement:", error);
          setShowMetalPercentage(false);
          setMetalRules([]);
        }
      } else {
        setShowMetalPercentage(false);
        setMetalRules([]);
      }
    };

    // Debounce the check to avoid too many API calls
    const timer = setTimeout(checkMetal, 500);
    return () => clearTimeout(timer);
  }, [formData.htsCode, formData.countryOfOrigin, checkMetalPercentage]);

  // Apply initialData when opening the modal
  useEffect(() => {
    if (open && initialData) {
      setFormData((prev) => ({
        ...prev,
        htsCode: initialData.htsCode ? formatHTSCode(String(initialData.htsCode).replace(/\D/g, "")) : prev.htsCode,
        countryOfOrigin: initialData.countryOfOrigin ?? prev.countryOfOrigin,
        metalPercentage: initialData.metalPercentage !== undefined && initialData.metalPercentage !== null
          ? String(initialData.metalPercentage)
          : prev.metalPercentage,
      }));
    }
  }, [open, initialData]);

  const handleReset = () => {
    setFormData({
      sku: "",
      name: "",
      description: "",
      htsCode: "",
      countryOfOrigin: "",
      metalPercentage: "",
    });
    setShowMetalPercentage(false);
    setMetalRules([]);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const metalPercentageValue = formData.metalPercentage ? Number(formData.metalPercentage) : undefined;
      
      const result = await createProduct({
        product: {
          sku: formData.sku,
          name: formData.name,
          description: formData.description || undefined,
          htsCode: formData.htsCode,
          countryOfOrigin: formData.countryOfOrigin,
          metalPercentage: metalPercentageValue,
          // Always pass clientId; TD Client uses their tenantId as clientId
          ...(clientId ? { clientId } : {}),
          // For TD Admin, also pass tenantId
          ...(isTDAdminTCD && { tenantId: selectedTenant?._id }),
        },
      }).unwrap();
      
      onProductCreated(result._id);
      
      // Trigger polling for the new product
      if (onTriggerPolling && result._id) {
        onTriggerPolling(result._id, 'creation');
      }
      
      handleClose();
    } catch (error) {
      console.error("Error creating product:", error);
    }
  };

  const isFormValid = () => {
    const baseValid = formData.sku && formData.name && formData.htsCode && formData.countryOfOrigin;
    
    // If metal percentage is shown and has a value, validate it
    if (showMetalPercentage && formData.metalPercentage) {
      const metalValue = Number(formData.metalPercentage);
      return baseValid && !isNaN(metalValue) && metalValue >= 0 && metalValue <= 100;
    }
    
    return baseValid;
  };

  return (
    <ModalV2
      show={open}
      setShow={handleClose}
      title="Add New Product"
      subTitle="Create a new product in your catalogue"
      buttonText="Create Product"
      buttonAction={handleSubmit}
      isConfirmButtonLoading={isLoading}
      buttonLoader={!isFormValid()}
      maxWidth="md"
      modalStyles={{
        "& .MuiPaper-root": {
          maxWidth: "800px",
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Grid container spacing={4}>
          {/* Basic Information Section */}
          <Grid item xs={12}>
            <Paper sx={{ 
              p: 3, 
              borderRadius: '12px', 
              border: '1px solid #e5e7eb',
              backgroundColor: '#fafbfc',
            }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <DescriptionIcon sx={{ color: '#0B494B' }} />
                <Typography variant="h6" fontWeight={600} color="#374151">
                  Basic Information
                </Typography>
              </Stack>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextInput
                    label="Product SKU"
                    type="text"
                    value={formData.sku}
                    handleChangeValue={(val) => setFormData({ ...formData, sku: val })}
                    required
                    placeholder="e.g., ELEC-001"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    label="Product Name"
                    type="text"
                    value={formData.name}
                    handleChangeValue={(val) => setFormData({ ...formData, name: val })}
                    required
                    placeholder="e.g., Smartphone X12"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    label="Description"
                    type="text"
                    value={formData.description}
                    handleChangeValue={(val) => setFormData({ ...formData, description: val })}
                    multiline
                    rows={3}
                    placeholder="Optional product description"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Trade Information Section */}
          <Grid item xs={12}>
            <Paper sx={{ 
              p: 3, 
              borderRadius: '12px', 
              border: '1px solid #e5e7eb',
              backgroundColor: '#fafbfc',
            }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <PublicIcon sx={{ color: '#0B494B' }} />
                <Typography variant="h6" fontWeight={600} color="#374151">
                  Trade Information
                </Typography>
              </Stack>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <CountrySelect
                    value={formData.countryOfOrigin}
                    onChange={(value) => setFormData({ ...formData, countryOfOrigin: value })}
                    countries={countrySummary?.getCountryOriginSummary || []}
                    isLoading={isLoadingCountries}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <HTSCodeSelect
                    value={formData.htsCode}
                    onChange={(value) => {
                      // Format the HTS code when it's changed
                      const formattedValue = formatHTSCode(value.replace(/\D/g, ""));
                      setFormData({ ...formData, htsCode: formattedValue });
                    }}
                    htsCodes={htsCodes?.getHTSCodesByCountry || []}
                    isLoading={isLoadingHTS}
                    required
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Metal Percentage Section - Dynamically Shown */}
          {(showMetalPercentage || isCheckingMetal) && (
            <Grid item xs={12}>
              <Paper sx={{ 
                p: 3, 
                borderRadius: '12px', 
                border: '1px solid #fbbf24',
                backgroundColor: '#fffbeb',
              }}>
                {isCheckingMetal ? (
                  <Stack spacing={2}>
                    <Skeleton variant="text" width="200px" height={30} />
                    <Skeleton variant="rectangular" height={56} />
                  </Stack>
                ) : (
                  <>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                      <FactoryIcon sx={{ color: '#f59e0b' }} />
                      <Typography variant="h6" fontWeight={600} color="#374151">
                        Metal Content
                      </Typography>
                      <Chip 
                        label="Section 232 Applies" 
                        size="small" 
                        color="warning"
                        icon={<img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />}
                      />
                    </Stack>

                    {metalRules.length > 0 && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={500} gutterBottom>
                          Metal percentage required for:
                        </Typography>
                        {metalRules.map((rule, index) => (
                          <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                            • {rule.name} - {(Number(rule.rules.data[0]?.rate) * 100).toFixed(0)}% duty rate
                          </Typography>
                        ))}
                      </Alert>
                    )}
                    
                    <TextInput
                      label="Metal Percentage (%)"
                      type="number"
                      value={formData.metalPercentage}
                      handleChangeValue={(val) => setFormData({ ...formData, metalPercentage: val })}
                      placeholder="e.g., 85.5"
                      helperText="Enter the percentage of steel/aluminum content (0-100). Leave empty to assume 100%."
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        inputProps: { 
                          min: 0, 
                          max: 100, 
                          step: 0.1 
                        }
                      }}
                      error={
                        formData.metalPercentage !== "" && 
                        (Number(formData.metalPercentage) < 0 || Number(formData.metalPercentage) > 100)
                      }
                    />
                    
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      <Tooltip title="The duty rate will be adjusted based on the metal percentage. For example, a 25% duty on 85% steel content = 21.25% effective rate.">
                        <span style={{ cursor: 'help' }}>
                          💡 This field is optional. If not provided, 100% metal content will be assumed.
                        </span>
                      </Tooltip>
                    </Typography>
                  </>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    </ModalV2>
  );
};

export default CreateProductForm; 